import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { tickets as ticketsApi, comments as commentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getActiveAgent, getNextPendingAgent, useAgent } from '../context/AgentContext';
import AgentPipeline from './AgentPipeline';
import AgentDetailPanel from './AgentDetailPanel';
import AlertModal from './AlertModal';

const TaskDetailModal = ({ task, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { refreshTask: contextRefreshTask } = useAgent();
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const active = getActiveAgent(task);
    if (active) return active.id;
    const next = getNextPendingAgent(task);
    if (next) return next.id;
    return 'enhance';
  });

  const [activeTab, setActiveTab] = useState('description');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [error, setError] = useState('');
  const [autoOrchestrate, setAutoOrchestrate] = useState(task.auto_orchestrate || false);
  const [orchestrateLoading, setOrchestrateLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, confirmText: 'Confirm' });
  const prevStatusRef = useRef(task.status);

  // Sync autoOrchestrate from prop when it changes via SSE
  useEffect(() => {
    setAutoOrchestrate(task.auto_orchestrate || false);
  }, [task.auto_orchestrate]);

  // Refresh task via AgentContext (single source of truth)
  const refreshTask = useCallback(async () => {
    await contextRefreshTask(task.id);
  }, [task.id, contextRefreshTask]);

  const loadPlan = useCallback(async () => {
    try {
      setLoadingPlan(true);
      const response = await ticketsApi.getPlan(task.id);
      setPlan(response.data.plan);
    } catch (err) {
      console.error('Failed to load plan:', err);
    } finally {
      setLoadingPlan(false);
    }
  }, [task.id]);

  const loadComments = useCallback(async () => {
    try {
      setLoadingComments(true);
      const response = await commentsApi.getForTicket(task.id);
      setComments(response.data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [task.id]);

  useEffect(() => {
    loadComments();
    loadPlan();
  }, [loadComments, loadPlan]);

  // Reload comments and plan when task status changes (via SSE)
  useEffect(() => {
    if (task.status !== prevStatusRef.current) {
      prevStatusRef.current = task.status;
      loadComments();
      loadPlan();
    }
  }, [task.status, loadComments, loadPlan]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      setSubmittingComment(true);
      const response = await commentsApi.create(task.id, newComment);
      setComments([...comments, response.data.comment]);
      setNewComment('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAgentUpdate = async () => {
    await refreshTask();
    await loadComments();
    await loadPlan();
  };

  const handleToggleOrchestrate = async () => {
    setOrchestrateLoading(true);
    try {
      const newValue = !autoOrchestrate;
      await ticketsApi.orchestrate(task.id, newValue);
      setAutoOrchestrate(newValue);
      await refreshTask();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle auto-orchestrate');
    } finally {
      setOrchestrateLoading(false);
    }
  };

  const handleRunAll = async () => {
    setOrchestrateLoading(true);
    try {
      await ticketsApi.orchestrate(task.id, true);
      setAutoOrchestrate(true);
      // Trigger enhance if it's the next pending agent
      const next = getNextPendingAgent(task);
      if (next?.id === 'enhance' && task.description) {
        await ticketsApi.enhance(task.id);
      }
      await refreshTask();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run all agents');
    } finally {
      setOrchestrateLoading(false);
    }
  };

  const handleRunNext = async () => {
    const next = getNextPendingAgent(task);
    if (!next) return;

    setOrchestrateLoading(true);
    try {
      if (next.id === 'enhance') {
        await ticketsApi.enhance(task.id);
      } else if (next.id === 'plan') {
        await ticketsApi.submitPlanJob(task.id);
      } else if (next.id === 'implement') {
        await ticketsApi.submitJob(task.id);
      } else if (next.id === 'review') {
        await ticketsApi.submitReviewJob(task.id);
      }
      await refreshTask();
      await loadComments();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to run ${next.label} agent`);
    } finally {
      setOrchestrateLoading(false);
    }
  };

  const handleDeleteTask = () => {
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to delete this task? This action cannot be undone.',
      confirmText: 'Yes, Delete',
      onConfirm: async () => {
        try {
          await ticketsApi.delete(task.id);
          onUpdate();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to delete task');
        }
      }
    });
  };

  const canDelete = user && (task.created_by?.id === user.id || user.is_admin);
  const nextAgent = getNextPendingAgent(task);

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-large task-detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h3>
              <span style={{ color: 'var(--text-tertiary)', fontFamily: "'JetBrains Mono', monospace", fontSize: '14px', marginRight: '8px' }}>
                TH-{task.id}
              </span>
              {task.title}
            </h3>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          {/* Pipeline Bar */}
          <div className="task-detail__pipeline-bar">
            <AgentPipeline
              pipeline={task.pipeline}
              selectedAgent={selectedAgent}
              onSelectAgent={setSelectedAgent}
            />
          </div>

          {error && <div className="alert alert-error" style={{ margin: '0 24px' }}>{error}</div>}

          {/* Two-column layout */}
          <div className="task-detail__body">
            {/* Left Panel: Content */}
            <div className="task-detail__left">
              {/* Tabs */}
              <div className="ticket-tabs">
                <button
                  className={`ticket-tab ${activeTab === 'description' ? 'active' : ''}`}
                  onClick={() => setActiveTab('description')}
                >
                  Description
                </button>
                <button
                  className={`ticket-tab ${activeTab === 'plan' ? 'active' : ''}`}
                  onClick={() => setActiveTab('plan')}
                  disabled={!plan}
                >
                  Plan {plan ? '\u2713' : ''}
                </button>
                <button
                  className={`ticket-tab ${activeTab === 'comments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('comments')}
                >
                  Comments ({comments.length})
                </button>
              </div>

              {/* Tab Content */}
              <div className="task-detail__tab-content">
                {activeTab === 'description' && (
                  <div className="ticket-detail-section">
                    <div className="ticket-description-display">
                      {task.description || 'No description provided'}
                    </div>
                  </div>
                )}

                {activeTab === 'plan' && (
                  <div className="ticket-detail-section">
                    {loadingPlan ? (
                      <div>Loading plan...</div>
                    ) : plan ? (
                      <div className="plan-display markdown-content">
                        <ReactMarkdown>{plan}</ReactMarkdown>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-tertiary)' }}>No plan available</div>
                    )}
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="comments-section" style={{ border: 'none', padding: 0 }}>
                    <div className="comments-list">
                      {loadingComments ? (
                        <div className="empty-comments">Loading comments...</div>
                      ) : comments.length === 0 ? (
                        <div className="empty-comments">No comments yet</div>
                      ) : (
                        comments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`comment-item ${comment.is_system ? 'system-comment' : ''}`}
                          >
                            <div className="comment-header">
                              <span className={`comment-author ${comment.is_system ? 'system' : ''}`}>
                                {comment.is_system && '\uD83E\uDD16 '}
                                {comment.author?.username || comment.author_name || 'Unknown'}
                              </span>
                              <span className="comment-date">
                                {formatRelativeTime(comment.created_at)}
                              </span>
                            </div>
                            <div className="comment-content">{comment.content}</div>
                          </div>
                        ))
                      )}
                    </div>

                    <form onSubmit={handleSubmitComment} className="comment-form">
                      <textarea
                        className="comment-textarea"
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        disabled={submittingComment}
                      />
                      <button
                        type="submit"
                        className="comment-submit"
                        disabled={submittingComment || !newComment.trim()}
                      >
                        {submittingComment ? 'Posting...' : 'Post Comment'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel: Agent Detail + Controls */}
            <div className="task-detail__right">
              <AgentDetailPanel
                task={task}
                selectedAgent={selectedAgent}
                onUpdate={handleAgentUpdate}
              />

              {/* Metadata */}
              <div className="task-detail__meta">
                <div className="task-detail__meta-row">
                  <span className="task-detail__meta-label">Priority</span>
                  <span className={`priority-badge priority-${task.priority}`}>{task.priority}</span>
                </div>
                <div className="task-detail__meta-row">
                  <span className="task-detail__meta-label">Status</span>
                  <span className="task-detail__meta-value">{task.status.replace(/_/g, ' ')}</span>
                </div>
                <div className="task-detail__meta-row">
                  <span className="task-detail__meta-label">Created by</span>
                  <span className="task-detail__meta-value">{task.created_by?.username}</span>
                </div>
                {task.job?.pr_url && (
                  <div className="task-detail__meta-row">
                    <span className="task-detail__meta-label">Pull Request</span>
                    <a
                      href={task.job.pr_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3b82f6', fontSize: '13px', textDecoration: 'none' }}
                    >
                      View PR
                    </a>
                  </div>
                )}
              </div>

              {/* Orchestration Controls */}
              <div className="task-detail__controls">
                <div className="task-detail__orchestrate-toggle">
                  <label className="task-detail__toggle-label">
                    <input
                      type="checkbox"
                      checked={autoOrchestrate}
                      onChange={handleToggleOrchestrate}
                      disabled={orchestrateLoading}
                    />
                    <span className="task-detail__toggle-text">Auto-orchestrate</span>
                  </label>
                </div>

                <div className="task-detail__action-buttons">
                  {nextAgent && (
                    <button
                      className="btn-secondary"
                      onClick={handleRunNext}
                      disabled={orchestrateLoading}
                      style={{ flex: 1 }}
                    >
                      {orchestrateLoading ? '...' : `Run ${nextAgent.label}`}
                    </button>
                  )}
                  {nextAgent && (
                    <button
                      className="btn-primary"
                      onClick={handleRunAll}
                      disabled={orchestrateLoading}
                      style={{ flex: 1 }}
                    >
                      {orchestrateLoading ? '...' : 'Run All'}
                    </button>
                  )}
                </div>

                {canDelete && (
                  <button
                    className="btn-danger"
                    onClick={handleDeleteTask}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    Delete Task
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        message={alertModal.message}
      />

      <AlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type="confirm"
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        onConfirm={() => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          if (confirmModal.onConfirm) confirmModal.onConfirm();
        }}
      />
    </>
  );
};

export default TaskDetailModal;
